'use client';

import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { Link } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { useIsTattooPage } from '@/hooks/useIsTattooPage';
import { useTattooBodyClass } from '@/hooks/useTattooBodyClass';
import { cn } from '@/lib/utils';

import { LogoImgPath } from '../../assets/images';
import Button from '../Button/Button';
import FlashMessage from '../FlashMessage';

const navigation = [
  { name: 'Piercing', href: '/' },
  { name: 'Tatouage', href: '/tatouage', flag: 'New' },
  { name: 'Informations', href: '#informations' },
  { name: 'Contact', href: '#contact' },
  { name: 'FAQ', href: '#faq' },
  { name: 'Prix', href: '/prix' },
];

const Header = () => {
  const isTattooPage = useIsTattooPage();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  useTattooBodyClass();

  const headerBg = isTattooPage ? 'bg-emerald-50/90' : 'bg-amber-50/90';
  const buttonClass = isTattooPage ? 'bg-emerald-600 hover:bg-emerald-700 text-white' : '';
  const menuBg = isTattooPage ? 'bg-emerald-50' : 'bg-amber-50';
  const accentColor = isTattooPage ? 'bg-emerald-600' : 'bg-amber-800';
  const linkHoverBg = isTattooPage ? 'hover:bg-emerald-100' : 'hover:bg-amber-100';
  const getHref = (href: string) => {
    if (isTattooPage && href.startsWith('#')) {
      return `/tatouage${href}`;
    }
    return href;
  };

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleCloseMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  return (
    <header className={cn('sticky inset-x-0 top-0 z-90 backdrop-blur-sm', headerBg)}>
      <FlashMessage />
      <nav
        className={cn(
          'mx-auto flex max-w-7xl items-center justify-between px-6 transition-all lg:px-8',
          isScrolled ? 'py-1' : 'py-4',
        )}
        aria-label="Global"
      >
        <div className="flex md:flex-1">
          <Link to="/" className="-m-1.5 p-1.5">
            <span className="sr-only">Azelysse Piercing & Tattoo</span>
            <img
              className="h-16 w-auto"
              src={LogoImgPath}
              alt="Azelysse logo"
              width={488}
              height={187}
            />
          </Link>
        </div>

        <div className="hidden md:flex md:gap-x-6 lg:gap-x-12">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={getHref(item.href)}
              className="relative text-sm leading-6 font-semibold text-gray-900"
            >
              {item.name}

              {item.flag ? (
                <span className="absolute -top-2 -right-6 rotate-10 rounded-lg bg-amber-300 px-2 text-xs">
                  {item.flag}
                </span>
              ) : null}
            </Link>
          ))}
        </div>
        <div className="hidden items-center gap-2 md:flex md:flex-1 md:justify-end">
          <Link to="/rendez-vous">
            <Button
              trackEvent="RendezVousClick"
              trackProps={{ location: 'header' }}
              className={cn('meeting-button', buttonClass)}
            >
              Prendre rendez-vous
            </Button>
          </Link>
        </div>

        {/* Hamburger button animé */}
        <div className="flex md:hidden">
          <button
            type="button"
            className="relative -m-2.5 flex h-10 w-10 items-center justify-center rounded-lg transition-colors hover:bg-black/5"
            onClick={() => setMobileMenuOpen(true)}
            aria-label="Ouvrir le menu"
          >
            <div className="flex w-5 flex-col items-center gap-1.25">
              <span
                className={cn('h-0.5 w-full rounded-full transition-all duration-300', accentColor)}
              />
              <span
                className={cn(
                  'h-0.5 w-3.5 self-end rounded-full transition-all duration-300',
                  accentColor,
                )}
              />
              <span
                className={cn('h-0.5 w-full rounded-full transition-all duration-300', accentColor)}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Menu mobile */}
      <Dialog as="div" className="md:hidden" open={mobileMenuOpen} onClose={handleCloseMenu}>
        <DialogBackdrop
          transition
          className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm transition-opacity duration-300 data-closed:opacity-0"
        />
        <DialogPanel
          transition
          className={cn(
            'fixed inset-y-0 right-0 z-100 flex w-full flex-col overflow-y-auto px-6 py-4 sm:max-w-sm',
            'transition-transform duration-300 ease-out data-closed:translate-x-full',
            menuBg,
          )}
        >
          {/* Header du menu */}
          <div className="flex items-center justify-between">
            <Link to="/" className="-m-1.5 p-1.5" onClick={handleCloseMenu}>
              <span className="sr-only">Azelysse Piercing & Tattoo</span>
              <img
                className="h-16 w-auto"
                src={LogoImgPath}
                alt="Azelysse logo"
                width={488}
                height={187}
              />
            </Link>
            {/* Bouton fermer (X) */}
            <button
              type="button"
              className="relative -m-2.5 flex h-11 w-11 items-center justify-center rounded-lg transition-colors hover:bg-black/5"
              onClick={handleCloseMenu}
              aria-label="Fermer le menu"
            >
              <div className="relative flex h-6 w-6 items-center justify-center">
                <span
                  className={cn('absolute h-0.75 w-full rotate-45 rounded-full', accentColor)}
                />
                <span
                  className={cn('absolute h-0.75 w-full -rotate-45 rounded-full', accentColor)}
                />
              </div>
            </button>
          </div>

          {/* Liens de navigation */}
          <div className="mt-10 flex flex-1 flex-col">
            <div className="space-y-1">
              {navigation.map((item, index) => (
                <Link
                  key={item.name}
                  to={getHref(item.href)}
                  className={cn(
                    'group relative flex items-center rounded-xl px-4 py-3 text-lg font-semibold text-gray-900 transition-colors',
                    linkHoverBg,
                    'animate-fade-in-up',
                  )}
                  style={{ animationDelay: `${index * 50 + 100}ms` }}
                  onClick={handleCloseMenu}
                >
                  {item.name}
                  {item.flag ? (
                    <span className="ml-2 rounded-full bg-amber-300 px-2 py-0.5 text-xs font-medium">
                      {item.flag}
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>

            {/* CTA en bas */}
            <div
              className="animate-fade-in-up mt-auto pt-6 pb-8"
              style={{ animationDelay: `${navigation.length * 50 + 150}ms` }}
            >
              <Link to="/rendez-vous" className="block">
                <Button
                  size="xl"
                  onClick={handleCloseMenu}
                  trackEvent="RendezVousClick"
                  trackProps={{ location: 'header' }}
                  className={cn('w-full text-base', buttonClass)}
                >
                  Prendre rendez-vous
                </Button>
              </Link>
            </div>
          </div>
        </DialogPanel>
      </Dialog>
    </header>
  );
};

export default Header;
